import React from "react";
import { Link, useLocation } from "react-router-dom";
import { CreditCard } from "lucide-react";
import PageRoutes from "@/utils/constants";
import { buildPayUrl, shouldShowPaymentAlert } from "@/utils/paymentRecovery";
import { useSubscriptionRecovery } from "@/v2/hooks/useSubscriptionRecovery";

/**
 * HTPTV-695 (v2): compact header indicator for a subscriber whose renewal is
 * failing (past_due / unpaid / incomplete). It sits in the header icon cluster,
 * explains itself on hover, and links straight to the fix. No dismiss: a failing
 * payment should stay visible until it is resolved.
 */
export function PaymentAlert() {
  const { subscription, pendingPayment } = useSubscriptionRecovery();
  const { pathname } = useLocation();

  // Don't nag on the checkout page itself: the user is already doing the thing
  // the indicator asks for, and the link would point at the current page.
  if (pathname.startsWith("/payment")) return null;
  if (!shouldShowPaymentAlert(subscription)) return null;

  // An outstanding invoice (an incomplete signup has no card at all) goes to
  // checkout to finish paying; everyone else goes to the account page to replace
  // the card that failed.
  const payUrl = buildPayUrl(pendingPayment);
  const label = payUrl
    ? "Your plan isn't active yet. Finish paying to start watching."
    : "Your payment didn't go through. Update your card to keep watching.";

  return (
    <Link
      to={payUrl ?? PageRoutes.AccountPage}
      aria-label={label}
      title={label}
      // Amber via inline style: the header's unlayered `a { color }` scoping
      // outranks a Tailwind text-* utility, so inline keeps the warning colour.
      className="relative flex size-9 items-center justify-center rounded-full transition-colors hover:bg-white/10"
      style={{ color: "#f59e0b" }}
    >
      <CreditCard className="size-5" />
    </Link>
  );
}
